import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoints } from '../endpoints/endpoints';
import {
  AimCountry,
  AimStateProvince,
  Currency,
  Division,
  DivisionSteward,
  DocumentSecurityLevel,
  Employee,
  ManagementType,
  SaleType,
  Stewardship,
  TransactionType
} from '../../models/AIMModels';
import { AssetType } from '../../models/PrismTransactionModels';
import { ReasonCode } from '../../models/OpportunityModels';
import { DivisionStewardshipParams, StateProvinceByStateParams } from '../../models/APIModels';
import { AimChecklistMilestone } from '../../models/TransactionTeamModels';
import { AIM_SERVICE } from '../../envUtils';

const AimApiQueryCreator = () => {
  let getToken: () => Promise<string> | string = () => '';
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };
  const api = createApi({
    reducerPath: 'lookupsApi',
    baseQuery: fetchBaseQuery({
      baseUrl: `${AIM_SERVICE}`,
      prepareHeaders: async (headers) => {
        const token = await getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
      }
    }),
    endpoints: (builder) => ({
      assetTypes: builder.query<AssetType[], null>({ query: () => endpoints.assetTypes }),
      managementTypes: builder.query<ManagementType[], null>({ query: () => endpoints.managementTypes }),
      divisions: builder.query<Division[], null>({ query: () => endpoints.divisions }),
      divisionStewards: builder.query<DivisionSteward[], string>({
        query: (aimDivision) => endpoints.divisionStewards(aimDivision)
      }),
      divisionStewardShip: builder.query<Stewardship, DivisionStewardshipParams>({
        query: ({ ...params }) => endpoints.divisionStewardShip(params)
      }),
      employees: builder.query<Employee[], null>({
        query: () => endpoints.employees
      }),
      divisionEmployees: builder.query<Employee[], string>({
        query: (aimDivision) => endpoints.divisionEmployees(aimDivision)
      }),
      saleTypes: builder.query<SaleType[], null>({ query: () => endpoints.saleTypes }),
      getDocumentSecurityLevels: builder.query<DocumentSecurityLevel[], null>({
        query: () => endpoints.documentSecurityLevels
      }),
      getDocumentSecurityLevel: builder.query<DocumentSecurityLevel, string>({
        query: (aimDocumentSecurityLevel) => endpoints.documentSecurityLevel(aimDocumentSecurityLevel)
      }),
      countries: builder.query<AimCountry[], null>({ query: () => endpoints.countries }),
      transactionTypes: builder.query<TransactionType[], null>({ query: () => endpoints.transactionTypes }),
      checklistMilestones: builder.query<AimChecklistMilestone[], null>({
        query: () => endpoints.checklistMilestones,
        transformResponse: (response: AimChecklistMilestone[]) =>
          response.sort((a, b) => a.sequence - b.sequence)
      }),
      reasonCodes: builder.query<ReasonCode[], null>({ query: () => endpoints.reasonCodes }),
      currencies: builder.query<Currency[], null>({ query: () => endpoints.currencies }),
      currency: builder.query<Currency, string>({ query: (currency) => endpoints.currency(currency) }),
      stewardships: builder.query<Stewardship, null>({ query: () => endpoints.stewardships }),
      stewardship: builder.query<Stewardship, string>({
        query: (aimStewardship) => endpoints.stewardship(aimStewardship)
      }),
      stewardshipsDivision: builder.query<Division, string>({
        query: (aimDivision) => endpoints.stewardshipsDivision(aimDivision)
      }),
      stewardshipByStewardDivision: builder.query<Stewardship, DivisionStewardshipParams>({
        query: (params) => endpoints.stewardShipByStewardDivision(params)
      }),
      employeesByDivision: builder.query<Employee[], string>({
        query: (aimDivision) => endpoints.employeesByDivision(aimDivision)
      }),
      employee: builder.query<Employee, string>({
        query: (aimEmployee) => endpoints.employee(aimEmployee)
      }),
      saleType: builder.query<SaleType, string>({
        query: (aimSaleType) => endpoints.saleType(aimSaleType)
      }),
      country: builder.query<AimCountry, string>({
        query: (country) => endpoints.country(country)
      }),
      stateProvinces: builder.query<AimStateProvince[], null>({
        query: () => endpoints.stateProvinces
      }),
      stateProvincesByCountry: builder.query<AimStateProvince[], string>({
        query: (country) => endpoints.stateProvincesByCountry(country)
      }),
      stateProvincesByState: builder.query<AimCountry, StateProvinceByStateParams>({
        query: (params) => endpoints.stateProvincesByState(params)
      }),
      transactionRejectionReasons: builder.query<string[], null>({
        query: () => endpoints.transactionRejectionReasons
      }),
      transactionFailReasons: builder.query<string[], null>({
        query: () => endpoints.transactionFailReasons
      })
    })
  });

  return { hooks: api, setTokenGetter };
};

/**
 * An RTK Query service providing React hooks for lookup and reference-data endpoints (e.g., divisions, countries, currencies, stewardships, employees) in the AIM platform, built with `fetchBaseQuery` and automatic Bearer-token injection.
 */
export const LookupsService = AimApiQueryCreator();
export const {
  useEmployeesQuery,
  useDivisionsQuery,
  useAssetTypesQuery,
  useManagementTypesQuery,
  useLazyDivisionStewardsQuery,
  useDivisionStewardShipQuery,
  useStateProvincesQuery,
  useLazyStateProvincesByCountryQuery,
  useSaleTypesQuery,
  useCountriesQuery,
  useReasonCodesQuery,
  useTransactionTypesQuery,
  useChecklistMilestonesQuery,
  useTransactionRejectionReasonsQuery,
  useTransactionFailReasonsQuery
} = LookupsService.hooks;
